import { useState } from 'react';
import { getStockLogoUrl, getFallbackLogoUrl } from '../../utils/stockLogos';

interface StockLogoProps {
  ticker: string;
  name?: string;
  website?: string;  // Company domain from API - preferred source for logo
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const sizeClasses = {
  sm: 'w-6 h-6',
  md: 'w-8 h-8',
  lg: 'w-10 h-10',
  xl: 'w-12 h-12',
};

const textSizeClasses = {
  sm: 'text-[9px]',
  md: 'text-[10px]',
  lg: 'text-xs',
  xl: 'text-sm',
};

export function StockLogo({ ticker, name, website, size = 'md', className = '' }: StockLogoProps) {
  // 0 = primary (API website or hardcoded), 1 = local SVG fallback, 2 = ticker fallback
  const [fallbackLevel, setFallbackLevel] = useState(0);

  // For fallback, show ticker symbol (max 4 chars)
  const displayTicker = ticker.toUpperCase().slice(0, 4);

  const handleError = () => {
    setFallbackLevel((prev) => prev + 1);
  };

  // Final fallback: clean ticker badge with subtle styling
  if (fallbackLevel >= 2) {
    return (
      <div
        className={`${sizeClasses[size]} ${textSizeClasses[size]} rounded-lg bg-[var(--color-bg-tertiary)] border border-[var(--color-border)] flex items-center justify-center font-bold text-[var(--color-text-muted)] font-mono tracking-tight ${className}`}
        title={name || ticker}
      >
        {displayTicker}
      </div>
    );
  }

  // Get URL based on fallback level
  let logoUrl: string;
  if (fallbackLevel === 0) {
    // Primary: use website from API if provided, otherwise use hardcoded mapping
    if (website) {
      logoUrl = `https://unavatar.io/${website}?fallback=false`;
    } else {
      logoUrl = getStockLogoUrl(ticker);
    }
  } else {
    // Fallback: try local SVG
    logoUrl = getFallbackLogoUrl(ticker);
  }

  return (
    <img
      src={logoUrl}
      alt={`${ticker} logo`}
      className={`${sizeClasses[size]} rounded-lg object-contain ${className}`}
      onError={handleError}
      loading="lazy"
      referrerPolicy="no-referrer"
    />
  );
}
